import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Client } from '../../../entities/client';
import { Ticket } from '../../../entities/ticket';
import { getClientTickets } from '../../../entities/client/api/clientApi';
import * as S from './ClientTicketsModal.styles';

interface ClientTicketsModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
}

export const ClientTicketsModal: React.FC<ClientTicketsModalProps> = ({
  isOpen,
  onClose,
  client,
}) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      loadTickets();
    }
  }, [isOpen, client.id]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await getClientTickets(client.id);
      setTickets(data);
    } catch (error) {
      console.error('Ошибка загрузки тикетов:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      'new': 'Новый',
      'active': 'Активный',
      'waiting_info': 'Ожидание информации',
      'in_programming': 'В разработке',
      'resolved': 'Решен',
      'closed': 'Закрыт',
      'spam': 'Спам',
      'duplicate': 'Дубликат',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      'new': '#3b82f6',
      'active': '#10b981',
      'waiting_info': '#f59e0b',
      'in_programming': '#8b5cf6',
      'resolved': '#6b7280',
      'closed': '#374151',
      'spam': '#ef4444',
      'duplicate': '#f97316',
    };
    return colors[status] || '#6b7280';
  };

  const getSiteLabel = (site: string): string => {
    if (site === 'hudle') return 'Hudle';
    if (site === 'tournament') return 'Турнир';
    if (site === 'unknown') return 'Неизвестен';
    return site;
  };

  const handleTicketClick = (ticketId: string) => {
    navigate(`/tickets/${ticketId}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <S.ModalOverlay onClick={onClose}>
      <S.ModalContent onClick={(e) => e.stopPropagation()}>
        <S.ModalHeader>
          <S.ModalTitle>История обращений</S.ModalTitle>
          <S.CloseButton onClick={onClose}>×</S.CloseButton>
        </S.ModalHeader>

        <S.ModalBody>
          {loading ? (
            <S.LoadingText>Загрузка...</S.LoadingText>
          ) : tickets.length === 0 ? (
            <S.EmptyState>
              <S.EmptyStateText>У клиента нет обращений</S.EmptyStateText>
            </S.EmptyState>
          ) : (
            <S.TicketsList>
              {tickets.map(ticket => (
                <S.TicketItem
                  key={ticket.id}
                  onClick={() => handleTicketClick(ticket.id)}
                >
                  <S.TicketHeader>
                    <S.TicketTitle>{ticket.title}</S.TicketTitle>
                    <S.TicketSite>{getSiteLabel(ticket.site)}</S.TicketSite>
                  </S.TicketHeader>
                  <S.TicketInfo>
                    <S.StatusBadge $color={getStatusColor(ticket.status)}>
                      {getStatusLabel(ticket.status)}
                    </S.StatusBadge>
                    <S.TicketDate>
                      {new Date(ticket.createdAt).toLocaleDateString('ru-RU')}
                    </S.TicketDate>
                  </S.TicketInfo>
                  {ticket.lastMessage && (
                    <S.MessagePreview>
                      {ticket.lastMessage.content.substring(0, 100)}
                      {ticket.lastMessage.content.length > 100 && '...'}
                    </S.MessagePreview>
                  )}
                </S.TicketItem>
              ))}
            </S.TicketsList>
          )}
        </S.ModalBody>
      </S.ModalContent>
    </S.ModalOverlay>
  );
};
